import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Bold, Italic, List, Strikethrough } from 'lucide-react';
import { sanitizeRichHtml } from '../utils/sanitizeRichHtml';

type Variant = 'document' | 'comment';

function isEmptySurface(el: HTMLElement | null): boolean {
  if (!el) return true;
  if (el.querySelector('img')) return false;
  return !el.textContent?.replace(/\u200b/g, '').trim();
}

export interface RichTextAreaProps {
  id?: string;
  value: string;
  onChange: (html: string) => void;
  placeholder: string;
  variant?: Variant;
  compact?: boolean;
  /** When true, no outer frame — for use inside a parent card (composer). */
  frameless?: boolean;
  /** When true with frameless, editor grows to fill remaining height (main draft in composer). */
  fillHeight?: boolean;
  className?: string;
  'aria-label'?: string;
}

export const RichTextArea: React.FC<RichTextAreaProps> = ({
  id,
  value,
  onChange,
  placeholder,
  variant = 'document',
  compact,
  frameless,
  fillHeight,
  className = '',
  'aria-label': ariaLabel,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const [bar, setBar] = useState<{ top: number; left: number } | null>(null);
  const [empty, setEmpty] = useState(true);

  const flush = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = sanitizeRichHtml(el.innerHTML);
    setEmpty(isEmptySurface(el));
    onChange(next);
  }, [onChange]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el || document.activeElement === el) return;
    const cur = el.innerHTML || '';
    const incoming = value || '';
    if (cur !== incoming) el.innerHTML = incoming;
    setEmpty(isEmptySurface(el));
  }, [value]);

  const updateBar = useCallback(() => {
    const root = rootRef.current;
    const el = editorRef.current;
    if (!root || !el) {
      setBar(null);
      return;
    }
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      setBar(null);
      return;
    }
    const range = sel.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) {
      setBar(null);
      return;
    }
    const text = sel.toString();
    if (!text.trim()) {
      setBar(null);
      return;
    }
    const rect = range.getBoundingClientRect();
    if (!rect.width && !rect.height) {
      setBar(null);
      return;
    }
    const pad = 6;
    setBar({
      top: rect.top + window.scrollY - pad - 36,
      left: rect.left + window.scrollX + rect.width / 2,
    });
  }, []);

  useLayoutEffect(() => {
    const onSel = () => updateBar();
    document.addEventListener('selectionchange', onSel);
    window.addEventListener('scroll', onSel, true);
    return () => {
      document.removeEventListener('selectionchange', onSel);
      window.removeEventListener('scroll', onSel, true);
    };
  }, [updateBar]);

  const apply = (cmd: string, cmdValue?: string) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    try {
      document.execCommand(cmd, false, cmdValue);
    } catch {
      /* ignore */
    }
    flush();
    requestAnimationFrame(updateBar);
  };

  const minH =
    variant === 'comment' ? (compact ? 'min-h-[56px]' : 'min-h-[64px]') : compact ? 'min-h-[140px]' : 'min-h-[200px]';
  const framed =
    `${minH} w-full resize-y overflow-auto rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-editor)] px-3 py-3 text-sm leading-relaxed text-[var(--text-primary)] shadow-[inset_0_1px_0_var(--input-well-inset)] outline-none transition-[border-color,box-shadow] focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--focus-ring)]`;
  /** Draft area inside unified composer (grows). */
  const framelessDraft =
    'w-full resize-none overflow-y-auto rounded-none border-0 bg-transparent px-3 py-2.5 text-sm leading-relaxed text-[var(--text-primary)] shadow-none outline-none focus:ring-0';
  const framelessGrow = `${framelessDraft} min-h-0 flex-1`;
  /** Compact “Cursor-style” comment strip: one line by default, short scroll if needed. */
  const commentMinH = compact ? 'min-h-[32px]' : 'min-h-[36px]';
  const framelessComment = `${commentMinH} max-h-[60px] w-full resize-none overflow-y-auto rounded-none border-0 bg-transparent px-2.5 pb-1 pt-2 text-[12px] leading-snug text-[var(--text-primary)] shadow-none outline-none focus:ring-0`;
  const framelessFixed = `${framelessDraft} ${minH}`;

  let editorClass: string;
  if (frameless === true && fillHeight === true) {
    editorClass = framelessGrow;
  } else if (frameless === true && variant === 'comment') {
    editorClass = framelessComment;
  } else if (frameless === true) {
    editorClass = framelessFixed;
  } else {
    editorClass = framed;
  }

  const rootClass =
    frameless === true && fillHeight === true ? `relative flex h-full min-h-0 flex-col ${className}` : `relative ${className}`;

  return (
    <div ref={rootRef} className={rootClass}>
      {bar && (
        <div
          className="pointer-events-auto fixed z-50 flex -translate-x-1/2 items-center gap-0.5 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)] px-1 py-0.5 shadow-lg"
          style={{ top: bar.top, left: bar.left }}
          role="toolbar"
          aria-label="Text formatting"
        >
          <ToolbarIcon label="Bold" onPress={() => apply('bold')}>
            <Bold className="h-3.5 w-3.5" />
          </ToolbarIcon>
          <ToolbarIcon label="Italic" onPress={() => apply('italic')}>
            <Italic className="h-3.5 w-3.5" />
          </ToolbarIcon>
          <ToolbarIcon label="Strikethrough" onPress={() => apply('strikeThrough')}>
            <Strikethrough className="h-3.5 w-3.5" />
          </ToolbarIcon>
          <div className="mx-0.5 h-4 w-px bg-[var(--border-subtle)]" aria-hidden />
          <ToolbarIcon label="Bullet list" onPress={() => apply('insertUnorderedList')}>
            <List className="h-3.5 w-3.5" />
          </ToolbarIcon>
        </div>
      )}

      {empty && (
        <div
          className={`pointer-events-none absolute select-none text-[var(--text-muted)] ${
            frameless === true && variant === 'comment'
              ? 'left-2.5 top-2 text-[12px] leading-snug'
              : frameless === true
                ? 'left-3 top-2.5 text-sm leading-relaxed'
                : 'left-3 top-3 text-sm leading-relaxed'
          }`}
          aria-hidden
        >
          {placeholder}
        </div>
      )}

      <div
        id={id}
        ref={editorRef}
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        contentEditable
        suppressContentEditableWarning
        spellCheck
        className={editorClass}
        onInput={flush}
        onBlur={() => {
          flush();
          setBar(null);
        }}
        onKeyUp={updateBar}
        onMouseUp={updateBar}
        onPaste={(e) => {
          e.preventDefault();
          const html = e.clipboardData.getData('text/html');
          const text = e.clipboardData.getData('text/plain');
          if (html) document.execCommand('insertHTML', false, sanitizeRichHtml(html));
          else if (text) document.execCommand('insertText', false, text);
          flush();
        }}
      />
    </div>
  );
};

const ToolbarIcon: React.FC<{
  label: string;
  onPress: () => void;
  children: React.ReactNode;
}> = ({ label, onPress, children }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    className="rounded p-1.5 text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]"
    onMouseDown={(e) => e.preventDefault()}
    onClick={() => onPress()}
  >
    {children}
  </button>
);
